import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react'
import { observer } from 'mobx-react-lite'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BoardCard } from 'app/components/action-center/BoardCard'
import { ContactBoardBulkBar } from 'app/components/action-center/ContactBoardBulkBar'
import type { SetStage } from 'app/components/action-center/treatmentBulkActions'
import { showClosedLostQuickModal } from 'app/components/action-center/ClosedLostQuickForm'
import { createClosedLostMilestone } from 'app/components/action-center/closedLostMilestone'
import { ExitDropZone } from 'app/components/action-center/ExitDropZone'
import {
  HYGIENE_SORTS,
  type HygieneBand,
  type HygieneBandId,
  type HygieneSortId,
  hygieneBandFromParamOrDefault,
  hygieneBandSearchTerms,
  hygieneDayWindowFromParam,
  hygieneSort,
} from 'app/components/action-center/hygieneBands'
import { readLocationIds } from 'app/components/action-center/locationParam'
import { LOCATION_RULE_COLUMN } from 'lib/filterRuleColumns'
import {
  activeSnoozes,
  getPreferredSnoozeDays,
  loadSnoozes,
  snoozeContact,
  useSnoozeVersion,
} from 'app/components/action-center/momentumStore'
import { useBoardTasks } from 'app/components/action-center/useBoardTasks'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { INBOX_TAB } from 'app/components/contacts/ContactRelatedObjects'
import { PageScroll } from 'app/components/layout/PageScroll'
import { ListBoard } from 'app/components/lists/board/ListBoard'
import { BulkSelectToggle } from 'app/components/lists/board/BulkSelectToggle'
import { ListBoardHeader } from 'app/components/lists/board/ListBoardHeader'
import { ListKindLine } from 'app/components/lists/ListKindLine'
import type { ListBoardCardDrag, ListBoardLaneDef, ListBoardSelection } from 'app/components/lists/board/boardTypes'
import { useBoardSelection } from 'app/components/lists/board/useBoardSelection'
import { stageLane } from 'app/components/lists/board/stageLane'
import { useListBoard } from 'app/components/lists/board/useListBoard'
import { useListStageModals } from 'app/components/lists/board/useListStageModals'
import { useStageVisibility } from 'app/components/lists/manual/useStageVisibility'
import { SortPicker } from 'app/components/action-center/SortPicker'
import { SystemListSetupCard } from 'app/components/lists/system/SystemListSetupCard'
import { Search } from 'app/components/Search'
import LocationFilter from 'app/components/table/filters/LocationFilter'
import { Button } from 'app/components/ui/button'
import { TableFiltersView } from 'app/components/ui/table-filters/table-filters'
import type { List, ListStage, SortDirection } from 'app/generated/graphql'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useSystemList } from 'app/hooks/useSystemList'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import type { ContactJson } from 'app/types/entities'
import type { ListBoardState } from 'app/types/list-board'
import type { FilterableColumn } from 'app/filter.utils'
import type { SystemListDef } from 'app/types/navigation'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { contactNameTerms } from 'lib/contactSearch'
import { logError } from 'lib/logging'
import { snoozeExclusionTerm, splitSystemStages } from 'lib/systemListBoard'

/** Same grace as `ContactBoardComponent`: a drawer close right behind a card press is a switch. */
const CARD_PRESS_GRACE_MS = 500

export const SystemListBoard = observer(function SystemListBoard({
  def,
  headerControls,
  titleActions,
  bandColumn,
  totalItems,
}: {
  def: SystemListDef
  headerControls: ReactNode
  titleActions?: ReactNode
  bandColumn?: FilterableColumn
  totalItems?: number
}) {
  const user = useUser()
  const workspace = useWorkspace()
  const [searchParams] = useSearchParams()
  const { list, failed, reload: reloadList } = useSystemList(def)
  const needsActionDateEnabled = !!useFeatureToggle(`needs-action-date`)
  const opts = useMemo(() => ({ needsActionDateEnabled }), [needsActionDateEnabled])
  const isHygiene = def.listKind === `hygiene`
  const bandParam = searchParams.get(`band`)
  const band: HygieneBand | undefined = isHygiene
    ? bandParam === null && def.defaultBand
      ? (def.defaultBand as HygieneBandId)
      : (hygieneDayWindowFromParam(bandParam) ?? hygieneBandFromParamOrDefault(bandParam))
    : undefined
  const [chosenSort, setChosenSort] = useState<HygieneSortId | undefined>()
  useEffect(() => setChosenSort(undefined), [band])
  const order = isHygiene ? hygieneSort(band, opts, def.queue, chosenSort) : undefined
  const sortSpec = order?.spec ?? def.queue.sort(opts)

  const snoozeVersion = useSnoozeVersion()
  useEffect(() => {
    if (workspace?.id) bg(loadSnoozes(workspace.id))
  }, [workspace?.id])
  const [nonce, setNonce] = useState(0)
  const snoozedIds = useMemo(
    () =>
      workspace?.id
        ? activeSnoozes(workspace.id)
            .filter((snooze) => snooze.queueId === def.queue.id)
            .map((snooze) => snooze.contactId)
        : [],
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- re-read when a snooze lands or the board reloads
    [workspace?.id, def.queue.id, snoozeVersion, nonce],
  )

  const filterColumns = useMemo(() => bandColumn && [LOCATION_RULE_COLUMN, bandColumn], [bandColumn])

  const filtersParam = searchParams.get(`filters`)
  const query = searchParams.get(`query`) ?? ``
  const filterTerms = useMemo(() => {
    const locationIds = readLocationIds(filtersParam)
    return [
      // The list's Scheduled stage holds the booked members, so the queue's "nobody booked" rule stays out.
      ...(isHygiene ? hygieneBandSearchTerms(band, { ...opts, includeBooked: true }) : def.queue.searchTerms(opts)),
      locationIds.length ? `locationId:(${locationIds.map((id) => `"${id}"`).join(` OR `)})` : ``,
      ...contactNameTerms(query),
      snoozeExclusionTerm(snoozedIds) ?? ``,
    ].filter(Boolean)
  }, [isHygiene, band, opts, def.queue, filtersParam, query, snoozedIds])

  const { lanes: laneStages, exits } = splitSystemStages(list?.stages ?? [], def.exitStageNames)
  const board = useListBoard({
    listId: list?.id,
    stageIds: laneStages.map((stage) => stage.id),
    filterTerms,
    sort: { field: sortSpec.field, direction: sortSpec.direction as SortDirection },
    ready: !!list,
  })

  const cardIds = useMemo(
    () =>
      Object.values(board.lanes).flatMap((lane) => lane.items.map((contact) => contact.id).filter(Boolean) as string[]),
    [board.lanes],
  )
  const visibleIds = useMemo(() => new Set(cardIds), [cardIds])
  const bulk = useBoardSelection(visibleIds)
  const bulkSelection = useMemo(
    () => ({
      rows: Object.values(board.lanes).flatMap((lane) =>
        lane.items.filter((contact) => contact.id && bulk.selected.has(contact.id)),
      ),
      clear: bulk.clear,
    }),
    [board.lanes, bulk.selected, bulk.clear],
  )
  // The board's own inputs do not change when the drawer edits a card, so the drawer says when to re-read.
  const [drawerNonce, setDrawerNonce] = useState(0)
  const noteDrawerChange = () => {
    setDrawerNonce((previous) => previous + 1)
    board.reload()
  }
  const tasks = useBoardTasks(cardIds, drawerNonce)

  const [drawerContact, setDrawerContact] = useState<ContactJson | undefined>(undefined)
  const [drawerTab, setDrawerTab] = useState<string | undefined>(undefined)
  const pressedCardAt = useRef(0)
  const closeDrawer = () => {
    if (Date.now() - pressedCardAt.current < CARD_PRESS_GRACE_MS) return
    setDrawerContact(undefined)
    setDrawerTab(undefined)
    board.reload()
  }

  // Drops only carry an id, and the Closed Lost modal and the toasts need the record.
  const findContact = (contactId: string) =>
    Object.values(board.lanes)
      .flatMap((lane) => lane.items)
      .find((contact) => contact.id === contactId)

  const stageById = new Map((list?.stages ?? []).map((stage) => [stage.id, stage]))
  // Only Active Leads' own exits: a Hygiene practice status may carry the same name.
  const isListExit = (stage: ListStage) =>
    def.listKind === `leads` && !!stage.system && !!def.exitStageNames?.includes(stage.name)
  const handleDrop = (contactId: string, stageId: string) => {
    const stage = stageById.get(stageId)
    const contact = findContact(contactId)
    if (!stage || !contact) return
    if (isListExit(stage) && stage.name === `Closed Lost`) {
      if (!user) return
      // The milestone is the one write: the server derives the lead status from it and the item
      // leaves the list, so the card is forgotten rather than moved.
      showClosedLostQuickModal({
        contact,
        user,
        onSubmit: (data) =>
          bg(
            createClosedLostMilestone(contactId, user.orgId, data)
              .then(() => board.forget(contactId))
              .catch((err) => {
                logError(`Error closing a lead from the Active Leads board`, err)
                notifyError(`Couldn't close that lead. Please try again.`)
              }),
          ),
        onCancel: () => {},
      })
      return
    }
    bg(
      board.move(contactId, stageId).then(({ ok }) => {
        if (!ok) return
        // The server took the item off the list: a card left in a lane no one draws would still be read.
        if (isListExit(stage)) board.forget(contactId)
        if (stage.name === `Scheduled`) notifySuccess(`Marked ${contact.name ?? `this contact`} as scheduled`)
      }),
    )
  }

  // The bulk move is a drag's write without the drag: the same exit forgetting, minus the
  // toasts, which the bar's results modal says once for the whole run. Closed Lost asks for a
  // reason one contact at a time, so it stays a drag.
  const bulkStages = [...laneStages, ...exits]
    .filter((stage) => !(isListExit(stage) && stage.name === `Closed Lost`))
    .map((stage) => ({ label: stage.name, value: stage.id }))
  const bulkSetStage: SetStage<ContactJson> = async (contactId, stageId, _known, opts) => {
    const outcome = await board.move(contactId, stageId, opts)
    const stage = stageById.get(stageId)
    if (outcome.ok && stage && isListExit(stage)) board.forget(contactId)
    return outcome
  }

  const renderCard = (contact: ContactJson, drag: ListBoardCardDrag) => (
    <BoardCard
      def={def.queue}
      contact={contact}
      dragging={drag.dragging}
      onDragStart={drag.onDragStart}
      onDragEnd={drag.onDragEnd}
      select={drag.select}
      onOpen={() => {
        setDrawerTab(undefined)
        setDrawerContact(contact)
      }}
      onText={() => {
        setDrawerTab(INBOX_TAB)
        setDrawerContact(contact)
      }}
      onSnooze={() => {
        if (!contact.id || !workspace?.id) return
        const days = getPreferredSnoozeDays()
        const until = new Date()
        until.setDate(until.getDate() + days)
        snoozeContact(workspace.id, contact.id, def.queue.id, until, user?.id)
        setNonce((previous) => previous + 1)
        board.reload()
        notifySuccess(`Snoozed ${contact.name ?? `this contact`} for ${days} days`)
      }}
      onPress={() => (pressedCardAt.current = Date.now())}
      tasks={contact.id ? tasks.get(contact.id) : undefined}
      workspaceId={workspace?.id}
    />
  )

  return (
    // `relative` docks the bulk bar to the page rather than to the scrolling content.
    <PageScroll className="relative" contentClassName="flex h-full flex-col pb-6 pt-8">
      <ListBoardHeader
        title={def.name}
        subheader={<ListKindLine kind="builtIn" />}
        totalItems={totalItems}
        actions={titleActions}
        controls={
          <>
            <Search />
            {filterColumns ? (
              <TableFiltersView
                columns={filterColumns}
                fixedRules={{ source: def.name, rules: def.fixedRules({ ...opts, includeBooked: true }) }}
                useOrOption={false}
              />
            ) : (
              <LocationFilter customSearchParam="filters" />
            )}
          </>
        }
        headerControls={headerControls}
        viewToggle={
          <div className="flex items-center gap-2">
            <BulkSelectToggle selecting={bulk.selecting} onToggle={bulk.toggleSelecting} />
            {order && <SortPicker options={HYGIENE_SORTS} value={order.current} onSelect={setChosenSort} />}
          </div>
        }
        banner={
          board.error && (
            <p className="mt-4 rounded-lg bg-surface-subtle px-3 py-2 text-body-xs leading-relaxed text-strong">
              {board.error}
            </p>
          )
        }
      />
      {list ? (
        <SystemListBoardLoaded
          def={def}
          list={list}
          board={board}
          laneStages={laneStages}
          exits={exits}
          renderCard={renderCard}
          selection={bulk.selection}
          onDrop={handleDrop}
          onChanged={() => {
            reloadList()
            board.reload()
          }}
        />
      ) : failed ? (
        <div className="flex items-center gap-3 p-8">
          <p className="text-body-sm text-subtle">{`Couldn't load this list.`}</p>
          <Button variant="secondary" size="sm" onClick={reloadList}>
            Try again
          </Button>
        </div>
      ) : (
        <></>
      )}
      {bulk.selecting && (
        <ContactBoardBulkBar
          selection={bulkSelection}
          stages={bulkStages}
          omitLeadStatus={def.listKind === `leads`}
          setStage={bulkSetStage}
          onExit={bulk.exit}
        />
      )}
      {/* The board re-reads on close and on change: neither reaches the lanes on its own. */}
      <ContactDrawer
        openDrawer={Boolean(drawerContact)}
        contact={drawerContact}
        initialTab={drawerTab}
        onClose={closeDrawer}
        onChange={(next) => {
          setDrawerContact(next)
          noteDrawerChange()
        }}
        onTaskUpdated={noteDrawerChange}
        onActivityAdded={noteDrawerChange}
      />
    </PageScroll>
  )
})

// Its own component so the stage modals, which need a loaded list, are never a conditional hook.
function SystemListBoardLoaded({
  def,
  list,
  board,
  laneStages,
  exits,
  renderCard,
  selection,
  onDrop,
  onChanged,
}: {
  def: SystemListDef
  list: List
  board: ListBoardState
  laneStages: ListStage[]
  exits: ListStage[]
  renderCard: (contact: ContactJson, drag: ListBoardCardDrag) => ReactNode
  selection?: ListBoardSelection
  onDrop: (contactId: string, stageId: string) => void
  onChanged: () => void
}) {
  const { openAddStage, openStageEditor } = useListStageModals({ list, onChanged })
  const canEditStatuses = useCheckPermission()([`org:pages:pipelines`, `pages:pipelines`])
  // Reads stay on every lane; only drawing follows the person's choice, as on a manual list.
  const { visibleStages } = useStageVisibility(list)
  const shownStages = visibleStages.flatMap((visible) => laneStages.filter((stage) => stage.id === visible.id))

  const lanes: ListBoardLaneDef<ContactJson, string>[] = shownStages.map((stage) =>
    stageLane(stage, board, {
      // `system` is null, not false, on a stage the workspace added.
      headerAction:
        stage.system || !canEditStatuses ? undefined : (
          <Button
            variant="icon"
            size="icon-sm"
            aria-label={`Edit ${stage.name}`}
            onClick={() => openStageEditor({ ...stage, visible: true })}
          >
            <PencilSimpleIcon size={14} weight="bold" />
          </Button>
        ),
    }),
  )

  return (
    <>
      <SystemListSetupCard def={def} list={list} onBuildOwn={openAddStage} onChanged={onChanged} />
      <ListBoard
        lanes={lanes}
        itemId={(contact) => contact.id ?? undefined}
        touchDrag
        selection={selection}
        onMove={onDrop}
        onExitDrop={onDrop}
        renderCard={renderCard}
        renderExits={
          exits.length > 0
            ? (drag) =>
                exits.map((stage) => (
                  <ExitDropZone
                    key={stage.id}
                    column={{ key: stage.id, label: stage.name, exit: true }}
                    draggingId={drag.draggingId}
                    touchOver={drag.touchOverKey === stage.id}
                    onDrop={(contactId) => {
                      drag.endDrag()
                      onDrop(contactId, stage.id)
                    }}
                    onOpenList={() => {}}
                  />
                ))
            : undefined
        }
        afterLanes={
          def.setupTemplate &&
          canEditStatuses && (
            <Button variant="ghost" className="mt-1 shrink-0 self-start" onClick={openAddStage}>
              <PlusIcon size={16} weight="bold" /> Add stage
            </Button>
          )
        }
      />
    </>
  )
}
